import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Box, Chip, SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FILTER_CHIP_HEIGHT, getShipsFromSupplierChipLabel } from '../../../constants/marketplace';
import { HOVER_MEDIA, PILL_BORDER_RADIUS } from '../../../constants/ui';
import { marketplaceSetSupplierFilterAction } from '../../../store/actions/marketplace';
import {
  selectCanFilterBySupplier,
  selectIsSupplierFilterOn,
  selectSupplierLock,
} from '../../../store/selectors/marketplace';

interface IProps {
  isDividerLeading?: boolean;
}

const chipSx: SxProps<Theme> = {
  height: FILTER_CHIP_HEIGHT,
  borderRadius: PILL_BORDER_RADIUS,
  fontWeight: 400,
  backgroundColor: 'background.paper',
  '& .MuiChip-icon': { fontSize: 16, ml: 1.25, color: 'action.active' },
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: 'action.hover' },
  },
  '&.MuiChip-filledPrimary': {
    border: 1,
    borderColor: ({ palette }) => alpha(palette.primary.main, 0.4),
    backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.focusOpacity),
    color: 'primary.main',
    '& .MuiChip-icon': { color: 'primary.main' },
    [HOVER_MEDIA]: {
      '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.2) },
    },
  },
};

const dividerSx: SxProps<Theme> = { width: '1px', height: 24, flexShrink: 0, bgcolor: 'divider' };

const chipIcon = <LocalShippingOutlinedIcon />;

export const SupplierFilterChip = ({ isDividerLeading = false }: IProps) => {
  const dispatch = useDispatch();
  const supplierLock = useSelector(selectSupplierLock);
  const isSupplierFilterOn = useSelector(selectIsSupplierFilterOn);
  const canFilterBySupplier = useSelector(selectCanFilterBySupplier);

  const label = useMemo(() => getShipsFromSupplierChipLabel(supplierLock?.name), [supplierLock?.name]);

  const handleToggle = useCallback(() => {
    dispatch(marketplaceSetSupplierFilterAction(!isSupplierFilterOn));
  }, [dispatch, isSupplierFilterOn]);

  if (!canFilterBySupplier) {
    return null;
  }

  const divider = <Box aria-hidden sx={dividerSx} />;

  return (
    <>
      {isDividerLeading && divider}
      <Chip
        icon={chipIcon}
        label={label}
        variant={isSupplierFilterOn ? 'filled' : 'outlined'}
        color={isSupplierFilterOn ? 'primary' : 'default'}
        onClick={handleToggle}
        aria-pressed={isSupplierFilterOn}
        data-action="supplier_filter_chip"
        sx={chipSx}
      />
      {!isDividerLeading && divider}
    </>
  );
};
